/**
 * Users tab (Phase 5 Action 49).
 *
 * Two lists, side by side conceptually:
 *   • Admins — tenant_users with role='admin', plus their magic-link
 *     state so operators can re-issue an invite in one click.
 *   • All portal users — every tenant_users row (admins + members),
 *     with their platform identities so support can trace "who is
 *     U-slack-id?" back to a portal email.
 *
 * The admin table is unchanged from the pre-tab layout — it fetches from
 * the provisioning namespace since that's where the invite/magic-link
 * plumbing lives. The all-users table is new to Action 49 and fetches
 * from /api/tenants/:id/users.
 */

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useOutletContext } from 'react-router-dom';
import { fetchJson, formatDate, formatRelative, type TenantContext } from './shared';

interface AdminRow {
  tenantUserId: string;
  email: string;
  name: string | null;
  status: 'invited' | 'active' | 'suspended' | 'deleted';
  invitedAt: string | null;
  linkExpiresAt: string | null;
  linkConsumedAt: string | null;
}

interface TenantUserRow {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'member';
  status: 'invited' | 'active' | 'suspended' | 'deleted';
  invitedAt: string | null;
  activatedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  platformIdentities: Array<{ platform: 'slack' | 'teams'; platformUserId: string }>;
}

export default function Users() {
  const { tenant } = useOutletContext<TenantContext>();

  const adminsQ = useQuery({
    queryKey: ['tenant-admins', tenant.id],
    queryFn: () => fetchJson<{ data: { admins: AdminRow[] } }>(`/api/provisioning/tenants/${tenant.id}/admins`),
  });

  const usersQ = useQuery({
    queryKey: ['tenant-users', tenant.id],
    queryFn: () => fetchJson<{ data: { users: TenantUserRow[] } }>(`/api/tenants/${tenant.id}/users`),
  });

  return (
    <>
      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Admins</h2>
          <Link
            to={`/fleet/${tenant.id}/admin-invite`}
            className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Invite admin
          </Link>
        </header>
        <div className="px-6 py-4 text-sm">
          {adminsQ.isLoading && <div className="text-slate-500">Loading admins…</div>}
          {adminsQ.data && adminsQ.data.data.admins.length === 0 && (
            <div className="text-slate-500">No admins yet. Click "Invite admin" to send a magic link.</div>
          )}
          {adminsQ.data && adminsQ.data.data.admins.length > 0 && (
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Email</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Magic link</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Expires</th>
                  <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adminsQ.data.data.admins.map((a) => {
                  let linkState: 'Active' | 'Consumed' | 'Expired' | '—';
                  if (a.linkConsumedAt) linkState = 'Consumed';
                  else if (!a.linkExpiresAt) linkState = '—';
                  else if (new Date(a.linkExpiresAt).getTime() > Date.now()) linkState = 'Active';
                  else linkState = 'Expired';
                  const linkPillClass =
                    linkState === 'Active' ? 'bg-amber-100 text-amber-800' :
                    linkState === 'Consumed' ? 'bg-emerald-100 text-emerald-800' :
                    linkState === 'Expired' ? 'bg-slate-200 text-slate-700' :
                    'bg-transparent text-slate-400';
                  const canReissue = a.status !== 'deleted';
                  return (
                    <tr key={a.tenantUserId}>
                      <td className="py-2 text-slate-900">
                        {a.email}
                        {a.name && <span className="ml-2 text-slate-500">{a.name}</span>}
                      </td>
                      <td className="py-2">
                        <span className={clsx(
                          'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                          a.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                          a.status === 'invited' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-200 text-slate-700',
                        )}>{a.status}</span>
                      </td>
                      <td className="py-2">
                        <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', linkPillClass)}>
                          {linkState}
                        </span>
                      </td>
                      <td className="py-2 text-slate-500">
                        {a.linkExpiresAt ? formatDate(a.linkExpiresAt) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2 text-right">
                        {canReissue ? (
                          <Link
                            to={`/fleet/${tenant.id}/admin-invite?email=${encodeURIComponent(a.email)}${a.name ? `&name=${encodeURIComponent(a.name)}` : ''}`}
                            className="rounded-md border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                          >
                            Re-issue link
                          </Link>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">All portal users</h2>
          <span className="text-xs text-slate-500">
            {usersQ.data ? `${usersQ.data.data.users.length} user${usersQ.data.data.users.length === 1 ? '' : 's'}` : ''}
          </span>
        </header>
        <div className="px-6 py-4 text-sm">
          {usersQ.isLoading && <div className="text-slate-500">Loading users…</div>}
          {usersQ.isError && (
            <div className="text-red-700">
              Could not load users: {usersQ.error instanceof Error ? usersQ.error.message : 'unknown error'}
            </div>
          )}
          {usersQ.data && usersQ.data.data.users.length === 0 && (
            <div className="text-slate-500">No users yet.</div>
          )}
          {usersQ.data && usersQ.data.data.users.length > 0 && (
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Email</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Role</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Platforms</th>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Last login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usersQ.data.data.users.map((u) => (
                  <tr key={u.id}>
                    <td className="py-2 text-slate-900">
                      {u.email}
                      {u.name && <span className="ml-2 text-slate-500">{u.name}</span>}
                    </td>
                    <td className="py-2">
                      <span className={clsx(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        u.role === 'admin' ? 'bg-primary-100 text-primary-800' : 'bg-slate-100 text-slate-700',
                      )}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-2">
                      <span className={clsx(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        u.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                        u.status === 'invited' ? 'bg-amber-100 text-amber-800' :
                        'bg-slate-200 text-slate-700',
                      )}>{u.status}</span>
                    </td>
                    <td className="py-2 text-slate-700 font-mono text-xs">
                      {u.platformIdentities.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        u.platformIdentities
                          .map((p) => `${p.platform}:${p.platformUserId}`)
                          .join(', ')
                      )}
                    </td>
                    <td className="py-2 text-slate-500">{formatRelative(u.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}
